// frontend/src/components/auth/RegisterSelection.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { User, Building, ArrowRight, Star, Shield, Zap, Users } from 'lucide-react';

interface RegisterSelectionProps {
  className?: string;
}

const RegisterSelection: React.FC<RegisterSelectionProps> = ({ className = '' }) => {
  return (
    <div className={`min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className="max-w-4xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Join Our Community
          </h2>
          <p className="mt-2 text-lg text-gray-600">
            Choose how you'd like to get started
          </p>
        </div>

        {/* Registration Options */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
          {/* Customer Registration */}
          <Link
            to="/register/customer"
            className="group relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] border border-gray-200 hover:border-primary-300"
          >
            <div className="p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
                    <User className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">I need services</h3>
                    <p className="text-sm text-gray-500">Join as a Customer</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
              </div>

              {/* Description */}
              <p className="text-gray-600 mb-6">
                Find trusted local service providers for all your needs. From home repairs to personal care, discover vetted professionals in your area.
              </p>

              {/* Features */}
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <Star className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0" />
                  <span>Access to verified service providers</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Shield className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  <span>Secure booking and payment protection</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Zap className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                  <span>Instant quotes and real-time messaging</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Users className="h-4 w-4 text-purple-500 mr-2 flex-shrink-0" />
                  <span>Read reviews from other customers</span>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-primary-600">Free to join</span>
                  <div className="flex items-center text-sm text-gray-500">
                    <span>Get started</span>
                    <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>

            {/* Popular Badge */}
            <div className="absolute -top-3 -right-3">
              <div className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                Popular
              </div>
            </div>
          </Link>

          {/* Partner Registration */}
          <Link
            to="/register/partner"
            className="group relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] border border-gray-200 hover:border-green-300"
          >
            <div className="p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                    <Building className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">I provide services</h3>
                    <p className="text-sm text-gray-500">Join as a Partner</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-green-600 transition-colors" />
              </div>

              {/* Description */}
              <p className="text-gray-600 mb-6">
                Grow your business by connecting with customers who need your services. Join our network of trusted professionals and increase your bookings.
              </p>

              {/* Features */}
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <Users className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                  <span>Connect with new customers daily</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Zap className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                  <span>Easy booking and schedule management</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Shield className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0" />
                  <span>Secure payment processing</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Star className="h-4 w-4 text-purple-500 mr-2 flex-shrink-0" />
                  <span>Build your reputation with reviews</span>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-600">Background check required</span>
                  <div className="flex items-center text-sm text-gray-500">
                    <span>Apply now</span>
                    <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>

            {/* Earning Potential Badge */}
            <div className="absolute -top-3 -right-3">
              <div className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                Earn More
              </div>
            </div>
          </Link>
        </div>

        {/* Additional Info */}
        <div className="text-center space-y-4 mt-12">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link
              to="/app/login"
              className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
            >
              Sign in here
            </Link>
          </p>
          
          {/* Benefits Section */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-8">
              <h4 className="font-semibold text-gray-900 mb-4">Why Choose Our Platform?</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <Shield className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <div className="font-medium text-gray-900">Verified & Trusted</div>
                  <div className="text-gray-600">All partners are background checked</div>
                </div>
                <div className="text-center">
                  <Zap className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                  <div className="font-medium text-gray-900">Quick & Easy</div>
                  <div className="text-gray-600">Book services in just a few clicks</div>
                </div>
                <div className="text-center">
                  <Star className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                  <div className="font-medium text-gray-900">Quality Guaranteed</div>
                  <div className="text-gray-600">Satisfaction guarantee on all services</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterSelection;